import { useEffect } from 'react';
import { toast } from 'react-toastify';
import StatGrid from './StatGrid';
import Filters from './Filters';
import { StatCharts } from './StatCharts';
import { useFilters } from '../../hooks/useFilters';
import { getPreviousPeriod } from '../../utils/dateUtils';
import { useShowCharts } from '../../hooks/useShowCharts';
import { describeQueryError, useGetStatQuery } from '../../services/statApi';

export default function StatPage() {
  const [filters, setFilters] = useFilters();
  const [showCharts] = useShowCharts();

  // RTK Query keys the cache by these args and cancels superseded requests
  // itself, so rapid filter changes can no longer let a slow old response
  // overwrite newer data.
  const { data, error, isError, isFetching, refetch, fulfilledTimeStamp } = useGetStatQuery({
    interval: filters.interval,
    project: filters.project,
    campaignId: filters.campaignId,
    start: filters.start,
    end: filters.end,
    timezone: filters.timezone,
  });

  // Same-length window immediately before the selected one. Skipped entirely
  // when comparison is off, so it costs nothing by default.
  const previousPeriod = filters.compare ? getPreviousPeriod(filters.start, filters.end) : null;
  const { data: previousData } = useGetStatQuery(
    {
      interval: filters.interval,
      project: filters.project,
      campaignId: filters.campaignId,
      start: previousPeriod?.start,
      end: previousPeriod?.end,
      timezone: filters.timezone,
    },
    { skip: !previousPeriod },
  );

  useEffect(() => {
    if (isError) toast.error(describeQueryError(error));
  }, [isError, error]);

  const stat = data ?? [];
  const updatedAt = fulfilledTimeStamp
    ? new Date(fulfilledTimeStamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <main className="my-4 md:my-8">
      {showCharts && (
        <StatCharts
          stat={stat}
          previousStat={previousData}
          filters={filters}
          onSelectProject={(project) => setFilters({ project, campaignId: '' })}
        />
      )}

      <div className="flex animate-slide-down flex-col gap-3 md:gap-4">
        <div className="rounded-lg border border-card-line border-t-card-line-top bg-card p-3 shadow-soft md:p-5">
          <Filters stat={stat} />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 text-[0.8rem] text-fg-subtle">
          <label className="mr-auto flex cursor-pointer items-center gap-2 text-fg-muted select-none">
            <input
              type="checkbox"
              checked={filters.compare}
              onChange={(e) => setFilters({ compare: e.target.checked })}
              className="size-4 cursor-pointer accent-accent"
            />
            Compare with previous period
          </label>
          {updatedAt && <span>Updated {updatedAt}</span>}
          <button
            type="button"
            onClick={refetch}
            disabled={isFetching}
            className="flex items-center gap-1.5 rounded-sm border border-line bg-surface-raised px-2.5 py-1.5 text-fg-muted transition-colors hover:border-accent hover:text-fg disabled:opacity-50"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={isFetching ? 'animate-spin-slow' : undefined}
            >
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
            Refresh
          </button>
        </div>

        <div className="overflow-hidden md:min-h-[400px]">
          <StatGrid
            stat={stat}
            filters={filters}
            isFetching={isFetching}
            error={isError ? describeQueryError(error) : null}
            hasData={data !== undefined}
            onDrillDown={({ project, campaignId }) =>
              setFilters({ project, campaignId: String(campaignId ?? '') })
            }
          />
        </div>
      </div>
    </main>
  );
}
